/**
 * Item flow (review, retry, unblock, impossible, pause), the agent bus (ask/answer), comments and updates, artifacts,
 * KPI samples, bet verdicts, watchdog escalations and the overdue alarm (decision 0035).
 */
import { FILE_MAX_BYTES, isFileUri, shaOfFileUri } from "@minga/protocol"
import { renderBriefMarkdown } from "../brief-sections"
import { overdueCandidates } from "../derived"
import { carriesChainTaint, documentOfPost } from "../documents"
import { contentHash } from "../hash"
import { numberOutputs, refOfPost } from "../knowledge/output-refs"
import { itemNamesKpi } from "../kpi-history"
import { KPI_AT_MAX, kpiPeriodStart } from "../kpi-period"
import { monitorOf, projectDay, timedCapOf, timedIntentKey } from "../monitors"
import {
  askerWhoLeft,
  isWorkingMember,
  lendOwnerOf,
  noteTrustOf,
  shapingBefore,
  shapingPatch,
} from "../multiplayer"
import { isOutput, OUTPUT_KINDS, openReviewCards, supersederOf } from "../outputs"
import { capabilityVerdict } from "../policy"
import {
  escalationOptions,
  escalationTarget,
  failStage,
  inReviewNextStep,
  isSystemEffect,
  openProposal,
  passStage,
  refreshHumanReview,
  retriesValidator,
  reviewStageOf,
  withdrawEscalations,
  workerBudgetReached,
} from "../runs"
import {
  agentOf,
  artifactOf,
  isOpenProposal,
  itemOf,
  kpiOf,
  leadOf,
  principalOf,
  proposalOf,
  recordOf,
  runOf,
} from "../state"
import type {
  Actor,
  Agent,
  Command,
  FlowCommand,
  Ids,
  Item,
  Principal,
  ProjectEvent,
  ProjectState,
} from "../types"
import { chainOfReason, heldLine } from "../wakes"
import { checkRouting } from "./graph"
import { actorTaint, forbidden, invalid, notFound, reject, type Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>

/** Is the actor the project lead (decision 0032: the lead supervises every item's flow)? */
const isLeadActor = (tx: Tx): boolean =>
  tx.actor._tag === "agent" && leadOf(tx.state)?.id === tx.actor.agentId

/** Is the agent the item's owner? */
const ownsItem = (t: Pick<Item, "owner">, agentId: Ids["AgentId"]): boolean =>
  t.owner?._tag === "agent" && t.owner.agentId === agentId

/** People may steer any item; agents the items they own, and the project lead every item (decision 0032). */
const mayFlow = (tx: Tx, t: Item, what: string) => {
  const a = tx.actor
  if (a._tag === "user") return
  if (a._tag === "agent" && ownsItem(t, a.agentId)) return
  if (isLeadActor(tx)) return
  if (a._tag === "system") return // option effects of a system escalation (flow only)
  forbidden("not_owner", `only a person, the item's owner or the project lead may ${what}`)
}

/**
 * An item escalated to a person (`needs_human`: a non-retryable stop, a spent budget, repeated failure, review rounds
 * exhausted) waits for that person: an agent never retries, unblocks or closes it past the card.
 */
export const mayPassEscalation = (tx: Tx, t: Item, what: string) => {
  if (tx.actor._tag !== "agent" || t.blocked?._tag !== "needs_human") return
  const card = proposalOf(tx.state, t.blocked.proposalId)
  // decision 0138 (rule-made cards): a loop the watchdog stopped is the lead's to pass while no budget a person set is
  // reached (then the person's own limit holds)
  if (
    isLeadActor(tx) &&
    card?.body._tag === "question" &&
    card.body.escalation?.cause === "loop" &&
    workerBudgetReached(tx.state, t) === null
  )
    return
  forbidden(
    "not_owner",
    `${t.ref} waits for a person (${card?.ref ?? "their card"}): it is theirs to ${what}; ask_human if you need it sooner`,
  )
}

/**
 * An agent never lifts a person's pause, and a role agent never another agent's (decision 0032). The project lead lifts
 * any agent's or the system's pause (0032; decision 0138 keeps it so); a person's pause, and the hold a person's answer
 * left on a gate (0041), stay theirs.
 */
export const mayResume = (tx: Tx, t: Item, what = "resume") => {
  const a = tx.actor
  if (a._tag !== "agent") return
  const by = t.pausedBy ?? null // absent (paused before decision 0032) counts as the system's pause
  if (by?._tag === "user") forbidden("not_owner", `a person paused ${t.ref}: ask_human to ${what} it`)
  if (by?._tag === "agent" && by.agentId !== a.agentId && !isLeadActor(tx)) {
    const handle = agentOf(tx.state, by.agentId)?.handle ?? "another agent"
    forbidden("not_owner", `@${handle} paused ${t.ref}`)
  }
}

const requireItem = (tx: Tx, id: Ids["ItemId"]): Item =>
  itemOf(tx.state, id) ?? notFound({ _tag: "item", id })

/**
 * Wave E (decision 0063, spec §3.2): the actor shaped an open item (new text, checks, a new assignee, a Retry of a
 * leaver's item; decision 0158: notes and answers are feedback and shape nothing): Asked by moves to them (an agent in a turn: the turn's asker) and a standing lend ends, as one
 * `ItemUpdated`. Nothing when neither changes.
 */
export const emitShaping = (tx: Tx, itemId: Ids["ItemId"]): void => {
  const t = itemOf(tx.state, itemId)
  if (!t || t.status === "done" || t.status === "cancelled") return
  const patch = shapingPatch(tx.state, t, tx.actor)
  if (patch.askedBy === undefined && patch.lentRunnerId === undefined) return
  tx.emit({ _tag: "ItemUpdated", itemId: t.id, patch, before: shapingBefore(t, patch) })
}

/**
 * LendRunner (wave E, decision 0063, §15 decision 2(a) "Run here, per item"). With a runner: the actor is that runner's
 * owner as the hub named it in a waiting run's `lendable` (core knows no runner records), and the item has such a
 * waiting worker or validator run. `null` ends the lend: any member may (it only narrows, like shaping the item).
 */
export const lendRunner = (tx: Tx, c: Cmd<"LendRunner">): void => {
  const t = requireItem(tx, c.itemId)
  const a = tx.actor
  if (a._tag !== "user") forbidden("actor_kind", "only a person lends their runner")
  if (t.status === "done" || t.status === "cancelled") invalid(`${t.ref} is ${t.status}`, "itemId")
  const current = t.lentRunnerId ?? null
  if (c.runnerId === null) {
    if (current === null) invalid(`${t.ref} has no lend to end`, "runnerId")
    if (!isWorkingMember(tx.state, a.userId)) forbidden("not_owner", "only a member ends a lend")
  } else {
    if (current === c.runnerId) invalid(`${t.ref} is already lent this runner`, "runnerId")
    const owner = lendOwnerOf(tx.state, t.id, c.runnerId)
    if (owner === undefined)
      invalid(`no run of ${t.ref} waits for that runner: nothing to lend it to`, "runnerId")
    if (owner !== a.userId) forbidden("not_owner", "only the runner's owner lends it")
  }
  tx.emit({
    _tag: "ItemUpdated",
    itemId: t.id,
    patch: { lentRunnerId: c.runnerId },
    before: { lentRunnerId: current },
  })
}

/** RetryItem · UnblockItem · AcceptImpossible · PauseItem (also valid as question-option effects). */
export const decideFlow = (tx: Tx, c: FlowCommand): void => {
  const t = requireItem(tx, c.itemId)
  switch (c._tag) {
    case "RetryItem": {
      mayFlow(tx, t, "retry an item")
      mayPassEscalation(tx, t, "retry")
      if (tx.actor._tag === "agent") {
        const agent = agentOf(tx.state, tx.actor.agentId)
        if (agent && capabilityVerdict(tx.state, agent, "spawn_workers", t.areaId) === "never")
          forbidden("policy_never", "spawn_workers is never for this agent")
      }
      // decision 0120: an item in review names the next step for its stage
      if (t.status === "in_review") invalid(inReviewNextStep(tx.state, t), "itemId")
      if (t.status !== "blocked" && !(t.status === "todo" && t.retry !== null))
        invalid(`${t.ref} is ${t.status}: nothing to retry`, "itemId")
      // TRI-05: an automatic continuation is already scheduled; an agent's retry keeps its backoff. It is refused (it
      // emits nothing, so retry.at stays) rather than accepted as a silent no-op that would drop its note; only a
      // person's Retry starts it now
      if (tx.actor._tag === "agent" && t.status === "todo" && t.retry !== null)
        invalid(
          `${t.ref} already restarts automatically in ${Math.max(1, Math.ceil((t.retry.at - tx.now) / 1000))} s: nothing to retry now; post a note with comment if the next attempt needs it`,
          "itemId",
        )
      withdrawEscalations(tx, t.id)
      // wave E (spec §3.2): pressing Retry on the item of someone who left is choosing to run their text as yours
      if (tx.actor._tag === "user" && askerWhoLeft(tx.state, t) !== null) emitShaping(tx, t.id)
      // a validator that stopped before its verdict is handed back blocked at its stage (rev 2.1): retrying it
      // re-runs the validator for the same round instead of sending the item back to a worker
      const retryValidator = retriesValidator(tx.state, t)
      // decision 0095: a route for the next attempt replaces the item's worker route (the validator's, when the
      // validator is retried) in the same step; the agent chooses the place, Minga does not
      if (c.routing !== undefined) {
        const kind = retryValidator ? "validator" : "worker"
        const routing = { ...(t.routing ?? {}), [kind]: c.routing }
        checkRouting(tx, routing, t.type, "routing", t.hands)
        // decision 0116: the item's own pin holds; a retry elsewhere first changes or clears it
        const pin = t.placement.requires
        if (
          (pin.runner !== undefined &&
            c.routing.runnerId !== undefined &&
            c.routing.runnerId !== pin.runner) ||
          (pin.profile !== undefined &&
            c.routing.profileId !== undefined &&
            c.routing.profileId !== pin.profile)
        )
          invalid(
            `${t.ref} is pinned by its placement (${pin.runner ? `runner ${pin.runner}` : `profile ${pin.profile}`}): retry without a route there, or change the pin with update_item placement first`,
            "routing",
          )
        const shaping = shapingPatch(tx.state, t, tx.actor)
        tx.emit({
          _tag: "ItemUpdated",
          itemId: t.id,
          patch: { routing, ...shaping },
          before: { routing: t.routing ?? null, ...shapingBefore(t, shaping) },
        })
      }
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId: t.id,
        from: t.status,
        to: retryValidator ? "in_review" : "todo",
        cause: { _tag: "retry", note: c.note ?? null },
        blocked: null,
        resolution: null,
      })
      return
    }
    case "UnblockItem": {
      mayFlow(tx, t, "unblock an item")
      mayPassEscalation(tx, t, "unblock")
      if (t.status === "in_review") invalid(inReviewNextStep(tx.state, t), "itemId")
      if (t.status !== "blocked") invalid(`${t.ref} is not blocked`, "itemId")
      withdrawEscalations(tx, t.id)
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId: t.id,
        from: "blocked",
        to: "todo",
        cause: { _tag: "unblocked", note: c.note },
        blocked: null,
        resolution: null,
      })
      // hold: pause it in the same transaction, so it never becomes ready and no worker is dispatched
      if (c.hold === true && !t.paused)
        tx.emit({ _tag: "ItemPaused", itemId: t.id, paused: true, by: principalOf(tx.actor), note: c.note })
      return
    }
    case "AcceptImpossible": {
      mayFlow(tx, t, "accept an impossible claim")
      if (t.status !== "blocked" || t.blocked?._tag !== "impossible_claimed")
        invalid(`${t.ref} has no impossible claim to accept`, "itemId")
      withdrawEscalations(tx, t.id)
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId: t.id,
        from: "blocked",
        to: "cancelled",
        cause: { _tag: "impossible_accepted", note: c.note },
        blocked: null,
        resolution: { _tag: "impossible", reason: c.note },
      })
      return
    }
    case "PauseItem": {
      mayFlow(tx, t, "pause an item")
      // decision 0094: already so is not an error (Applied, no event)
      if (t.paused === c.paused) return
      if (!c.paused) mayResume(tx, t)
      tx.emit({
        _tag: "ItemPaused",
        itemId: t.id,
        paused: c.paused,
        by: principalOf(tx.actor),
        note: c.note ?? null,
      })
      return
    }
  }
}

/** The owner's review at the `agent` stage (or a person or the lead standing in for it). */
export const recordReview = (tx: Tx, c: Cmd<"RecordReview">): void => {
  const t = requireItem(tx, c.itemId)
  const a = tx.actor
  if (a._tag === "agent" && !ownsItem(t, a.agentId) && leadOf(tx.state)?.id !== a.agentId)
    forbidden("not_owner", "only the item's owner or the project lead reviews at the agent stage")
  if (t.status !== "in_review") invalid(`${t.ref} is ${t.status}, not in review`, "itemId")
  // decision 0120: an index past the stages (left by a ChecksRevised before 0120) is reviewed like a last agent stage
  const stage = reviewStageOf(t)
  if (stage !== "agent" && stage !== "past_end") invalid(inReviewNextStep(tx.state, t), "itemId")
  if (c.round !== t.review.round) invalid(`review round is ${t.review.round}`, "round")
  tx.emit({
    _tag: "ReviewRecorded",
    itemId: t.id,
    round: c.round,
    stage: "agent",
    verdict: c.verdict,
    notes: c.notes,
    findings: c.findings,
    runId: null,
  })
  if (c.verdict === "approve") passStage(tx, t.id, "approve")
  else failStage(tx, t.id, "changes_requested", null, c.notes)
}

const HANDLE = /(^|[^A-Za-z0-9_])@([a-z0-9][a-z0-9_-]{0,31})/gi

/**
 * Mentions are parsed server-side at write time: people and agents share one @handle namespace. Decision 0139: handles
 * match case-insensitively (`@Lea` is `@lea`), as `resolveRef` already does.
 */
export const parseMentions = (s: ProjectState, body: string): ReadonlyArray<Principal> => {
  const out: Principal[] = []
  for (const m of body.matchAll(HANDLE)) {
    const h = (m[2] ?? "").toLowerCase()
    const member = s.members.find((x) => x.handle?.toLowerCase() === h)
    if (member && !out.some((p) => p._tag === "user" && p.userId === member.userId))
      out.push({ _tag: "user", userId: member.userId })
    const agent = s.agents.find((x) => x.handle.toLowerCase() === h && x.status !== "retired")
    if (agent && !out.some((p) => p._tag === "agent" && p.agentId === agent.id))
      out.push({ _tag: "agent", agentId: agent.id })
  }
  return out
}

/** Wave A §7: a paused agent keeps its wakes (they wait in its inbox for the resume); a retired one gets none. */
const wakeable = (a: { readonly status: string } | undefined): boolean =>
  a !== undefined && (a.status === "active" || a.status === "paused")

/**
 * A person's @mention wakes an active or paused agent (untrusted if not its owner; decision 0154: nor a working member
 * of the project). Wave A §4.9: an agent's @mention
 * wakes the mentioned agent too (active or paused, never itself; untrusted when the acting run is tainted), as
 * `agent_mention`. A worker's mentions wake nobody.
 */
const mentionWakes = (s: ProjectState, actor: Actor, mentions: ReadonlyArray<Principal>) => {
  if (actor._tag === "user")
    return mentions.flatMap((m) => {
      if (m._tag !== "agent") return []
      const a = agentOf(s, m.agentId)
      return a && wakeable(a) ? [{ agentId: a.id, untrusted: !noteTrustOf(s, a.ownerId)(actor.userId) }] : []
    })
  if (actor._tag === "agent") {
    const untrusted = actorTaint(s, actor) === "tainted"
    return mentions.flatMap((m) => {
      if (m._tag !== "agent" || m.agentId === actor.agentId) return []
      const a = agentOf(s, m.agentId)
      return a && wakeable(a) ? [{ agentId: a.id, untrusted }] : []
    })
  }
  return []
}

const requireTarget = (tx: Tx, target: Cmd<"PostComment">["target"]) => {
  if (!recordOf(tx.state, target)) notFound(target)
}

export const postComment = (tx: Tx, c: Cmd<"PostComment">): void => {
  requireTarget(tx, c.target)
  if (c.health !== undefined && c.target._tag !== "goal" && c.target._tag !== "item")
    invalid("an update (with health) goes on a goal or an item", "target")
  // decision 0049: a decision comment records what was decided, why and where it came from, on the record it changed
  if (c.decision === true && c.health !== undefined) invalid("a decision comment has no health", "decision")
  if (c.decision === true && c.target._tag !== "item" && c.target._tag !== "goal")
    invalid("a decision comment goes on the item or goal it changed", "target")
  const mentions = parseMentions(tx.state, c.body)
  const wakes = [...mentionWakes(tx.state, tx.actor, mentions)]
  // a person's note or update on an agent's item reaches its owner without an @mention (decision 0035, note 5); a
  // paused owner keeps it for the resume (Wave A §7)
  const actor = tx.actor
  if (actor._tag === "user" && c.target._tag === "item") {
    const item = itemOf(tx.state, c.target.id)
    const owner = item?.owner?._tag === "agent" ? agentOf(tx.state, item.owner.agentId) : undefined
    if (owner && wakeable(owner) && !wakes.some((w) => w.agentId === owner.id))
      wakes.push({ agentId: owner.id, untrusted: !noteTrustOf(tx.state, owner.ownerId)(actor.userId) })
  }
  tx.emit({
    _tag: "CommentPosted",
    comment: {
      ...tx.recordMeta(),
      contentSeq: (tx.state.seq + 1) as Ids["Seq"],
      id: c.commentId,
      target: c.target,
      threadId: c.threadId ?? c.commentId,
      kind: c.health !== undefined ? "update" : c.decision === true ? "decision" : "note",
      body: c.body,
      mentions,
      ask: null,
      answers: null,
      wakes,
      tainted: actorTaint(tx.state, tx.actor) === "tainted",
      editedAt: null,
      resolvedAt: null,
      ...(c.health !== undefined ? { health: c.health } : {}),
    },
  })
  // decision 0158 (Jannik, onboarding week: "Becky made it run as Becky"): a note is feedback. It is packed into the
  // next attempt and wakes the item's agent (0154), but it never changes who the item runs as (wave E's shaping is for
  // new text, checks, an assignee or a Retry)
}

export const editComment = (tx: Tx, c: Cmd<"EditComment">): void => {
  const existing =
    tx.state.openAsks.find((x) => x.id === c.commentId) ?? tx.ctx.history?.comment?.(c.commentId)
  if (!existing) notFound({ _tag: "comment", id: c.commentId })
  if (
    tx.actor._tag !== "user" ||
    existing.createdBy._tag !== "user" ||
    existing.createdBy.userId !== tx.actor.userId
  )
    forbidden("not_owner", "people edit their own comments; agents post corrections")
  tx.emit({ _tag: "CommentEdited", commentId: c.commentId, body: c.body, before: existing.body })
}

export const resolveThread = (tx: Tx, c: Cmd<"ResolveThread">): void => {
  tx.emit({ _tag: "ThreadResolved", threadId: c.threadId })
}

/**
 * Ask any agent (`to`, Wave A §4.8); without `to`, one level up: worker → its item's owner agent, role agent → lead.
 * The lead names whom it asks (it has no agent above it); people are asked with ask_human. A paused agent can be
 * asked: the wake waits for its resume.
 */
export const ask = (tx: Tx, c: Cmd<"Ask">): void => {
  requireTarget(tx, c.target)
  const a = tx.actor
  let to: Ids["AgentId"] | null = null
  if (c.to !== undefined) {
    if (a._tag !== "agent")
      forbidden("actor_kind", "only an agent names whom it asks (a worker asks its item's owner)")
    const target = agentOf(tx.state, c.to)
    if (!target || target.status === "retired") invalid("no such agent in this project", "to")
    if (target.id === a.agentId) invalid("you can't ask yourself", "to")
    to = target.id
  } else if (a._tag === "worker") {
    const owner = itemOf(tx.state, a.itemId)?.owner
    to = owner?._tag === "agent" ? owner.agentId : a.agentId
  } else if (a._tag === "agent") {
    const me = agentOf(tx.state, a.agentId)
    if (me?.role === "lead")
      invalid('name the agent you ask: `to: "@handle"`; ask a person with ask_human', "to")
    to = leadOf(tx.state)?.id ?? null
  }
  if (!to) invalid("there is no agent one level up to ask", "target")
  tx.emit({
    _tag: "CommentPosted",
    comment: {
      ...tx.recordMeta(),
      contentSeq: (tx.state.seq + 1) as Ids["Seq"],
      id: c.commentId,
      target: c.target,
      threadId: c.commentId,
      kind: "ask",
      body: c.body,
      mentions: [{ _tag: "agent", agentId: to }],
      ask: { to, state: "open" },
      answers: null,
      wakes: [{ agentId: to, untrusted: actorTaint(tx.state, a) === "tainted" }],
      tainted: actorTaint(tx.state, a) === "tainted",
      editedAt: null,
      resolvedAt: null,
    },
  })
}

/** Only the addressed agent answers. An item blocked on the ask goes back to todo (the answer joins the handoff). */
export const answer = (tx: Tx, c: Cmd<"Answer">): void => {
  const askC = tx.state.openAsks.find((x) => x.id === c.askId) ?? notFound({ _tag: "comment", id: c.askId })
  const a = tx.actor
  if (a._tag !== "agent" || askC.ask?.to !== a.agentId)
    forbidden("not_owner", "only the addressed agent answers an ask")
  tx.emit({
    _tag: "CommentPosted",
    comment: {
      ...tx.recordMeta(),
      contentSeq: (tx.state.seq + 1) as Ids["Seq"],
      id: c.commentId,
      target: askC.target,
      threadId: askC.threadId,
      kind: "answer",
      body: c.body,
      mentions: [],
      ask: null,
      answers: askC.id,
      wakes: [],
      tainted: actorTaint(tx.state, a) === "tainted",
      editedAt: null,
      resolvedAt: null,
    },
  })
  tx.emit({ _tag: "AskStateChanged", askId: askC.id, state: "answered", by: c.commentId })
  // decision 0158: an answer is feedback the next attempt reads; it never changes who the item runs as
  for (const t of tx.state.items)
    if (t.status === "blocked" && t.blocked?._tag === "question" && t.blocked.askId === askC.id)
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId: t.id,
        from: "blocked",
        to: "todo",
        cause: { _tag: "unblocked", note: "ask answered" },
        blocked: null,
        resolution: null,
      })
}

const FS_ROOT =
  /^\/(Users|home|root|tmp|private|var|Volumes|mnt|media|opt|etc|srv|usr|Library|System|Applications|workspace|workspaces)(\/|$)/

/** A link that names a file on one machine: `file:`, `~/…`, a Windows drive, or an absolute path under a filesystem root. */
const isLocalFileLink = (uri: string): boolean =>
  /^file:/i.test(uri) || /^~(\/|$)/.test(uri) || /^[a-z]:[\\/]/i.test(uri) || FS_ROOT.test(uri)

export const postArtifact = (tx: Tx, c: Cmd<"PostArtifact">): void => {
  const a = tx.actor
  if (artifactOf(tx.state, c.artifactId)) invalid("artifact id already exists", "artifactId")
  if (c.itemId && !itemOf(tx.state, c.itemId)) notFound({ _tag: "item", id: c.itemId })
  if (a._tag === "worker" && c.itemId !== a.itemId)
    forbidden("not_owner", "a worker posts artifacts on its own item")
  if (c.supersedes && !artifactOf(tx.state, c.supersedes))
    invalid("superseded artifact does not exist", "supersedes")
  // wave DOC (decision 0068): the document rules (spec §3); `doc` is what the new artifact carries
  const doc = documentOfPost(tx, c)
  // TRI-06: a local file link only opens on the machine that wrote it. An app-relative link (`/p/…`) stays allowed;
  // only `/` paths under a filesystem root are refused. Workers and agents are told to upload; people to attach the file
  if (isLocalFileLink(c.uri))
    invalid(
      tx.actor._tag === "worker" || tx.actor._tag === "agent"
        ? "a file:// link only opens on the machine that wrote it: post the file with path (the runner uploads it), or link something others can open"
        : "a local file link only opens on your machine: upload the file instead, or link something others can open",
      "uri",
    )
  // decision 0050: an uploaded file names its bytes truthfully, and a new version goes on the same item
  if (isFileUri(c.uri)) {
    const sha = shaOfFileUri(c.uri)
    if (sha === null) invalid("an r2 URI is r2://artifacts/<sha256>", "uri")
    if (c.sha256 !== sha) invalid("sha256 must match the URI", "sha256")
    if (c.sizeBytes === null || c.sizeBytes > FILE_MAX_BYTES || c.contentType === null)
      invalid("a file needs its size (≤ 25 MB) and content type", "sizeBytes")
  }
  if (c.supersedes) {
    const old = artifactOf(tx.state, c.supersedes)
    if (old && old.itemId !== c.itemId) invalid("a new version goes on the same item", "supersedes")
    const newer = supersederOf(tx.state, c.supersedes)
    if (newer) invalid(`already superseded by ${newer.id}: supersede that one`, "supersedes")
    // decision 0050: a new version of an output is an output (a file of an output kind), so it stays a pick
    if (old && isOutput(old) && !(OUTPUT_KINDS.has(c.kind) && shaOfFileUri(c.uri) !== null))
      invalid("a new version of an output is an uploaded file of an output kind", "supersedes")
    // decision 0050: while a person picks on the open review card, only the item's owner or the lead replaces a pick
    const t = c.itemId ? itemOf(tx.state, c.itemId) : undefined
    if (
      a._tag === "agent" &&
      t?.status === "in_review" &&
      t.review.stages[t.review.stage] === "human" &&
      openReviewCards(tx.state, t.id).length > 0 &&
      !ownsItem(t, a.agentId) &&
      !isLeadActor(tx)
    )
      forbidden(
        "not_owner",
        "a person is reviewing this item: only its owner or the lead posts a new version",
      )
  }
  // decision 0040: each attempt re-posting the same PR or file is one artifact, not a pile (evidence keeps citing it)
  if (c.itemId && !c.supersedes) {
    const same = tx.state.artifacts.find(
      (x) =>
        x.itemId === c.itemId &&
        x.kind === c.kind &&
        x.uri === c.uri &&
        !tx.state.artifacts.some((y) => y.supersedes === x.id),
    )
    if (same)
      reject({
        _tag: "Duplicate",
        existing: { _tag: "artifact", id: same.id },
        reason: "already recorded",
      })
  }
  const runId = a._tag === "agent" || a._tag === "worker" ? a.runId : null
  tx.emit({
    _tag: "ArtifactPosted",
    artifact: {
      ...tx.recordMeta(),
      contentSeq: (tx.state.seq + 1) as Ids["Seq"],
      id: c.artifactId,
      itemId: c.itemId,
      runId,
      kind: c.kind,
      title: c.title,
      uri: c.uri,
      contentType: c.contentType,
      sizeBytes: c.sizeBytes,
      sha256: c.sha256,
      preview: c.preview,
      interfaceKey: c.interfaceKey,
      // decision 0050: `untrusted` (a file from outside the project) can only raise taint; decision 0068: so do the bytes
      // of a tainted version of the same chain (a Restore)
      tainted:
        actorTaint(tx.state, a) === "tainted" || c.untrusted === true || carriesChainTaint(tx.state, c),
      supersedes: c.supersedes,
      state: "draft",
      // wave K: the output's short ref, minted here and carried in the event like P-n (after the outputs of a state
      // from before the wave, which evolve numbers first). A new version keeps its chain's ref (one O-n per chain)
      ref: refOfPost(numberOutputs(tx.state), c.supersedes),
      ...(doc !== undefined ? { doc } : {}),
    },
  })
  // decision 0050: an open human review card follows the item's outputs
  if (c.itemId) refreshHumanReview(tx, c.itemId)
}

export const setArtifactState = (tx: Tx, c: Cmd<"SetArtifactState">): void => {
  const art = artifactOf(tx.state, c.artifactId) ?? notFound({ _tag: "artifact", id: c.artifactId })
  const a = tx.actor
  if (a._tag === "agent") {
    const t = art.itemId ? itemOf(tx.state, art.itemId) : undefined
    if (!t || !ownsItem(t, a.agentId))
      forbidden("not_owner", "only a person or the item's owner sets artifact state")
    // decision 0050: a person's pick or ✕ stands; an agent posts a new version instead
    if (art.state !== "draft" && art.updatedBy._tag === "user")
      forbidden("not_owner", `a person set this artifact ${art.state}; post a new version instead`)
  }
  if (art.state === c.state) invalid(`artifact is already ${c.state}`, "state")
  tx.emit({ _tag: "ArtifactStateChanged", artifactId: art.id, state: c.state, before: art.state })
}

/** Decision 0098: at most this many characters in one brief (a short note, not a report). */
export const BRIEF_MAX_CHARS = 2_000

/**
 * Decision 0098: an agent's brief to a person. Agents only; the text is the agent's own words (what happened, what's
 * next, what it needs from them, by ref). Decision 0130 (0126 §9): one brief per person, `to` a working member of the
 * project (default the agent's keeper). No cadence rule here: the instructions and the daily check's `brief:` loose end
 * say when and to whom.
 */
export const sendBrief = (tx: Tx, c: Cmd<"SendBrief">): void => {
  const a = tx.actor
  if (a._tag !== "agent") forbidden("actor_kind", "only an agent sends a brief")
  const agent = agentOf(tx.state, a.agentId) ?? notFound({ _tag: "agent", id: a.agentId })
  // decision 0144: a sectioned brief carries its sections; its markdown (for every older reader) is rendered from them
  // unless the agent also wrote one
  const sections = c.sections
  if (sections && sections.headline.trim() === "") invalid("the headline is empty", "sections.headline")
  const body = (c.body.trim() === "" && sections ? renderBriefMarkdown(sections) : c.body).trim()
  if (body === "") invalid("the brief is empty", "body")
  if (body.length > BRIEF_MAX_CHARS)
    invalid(
      `a brief is at most ${BRIEF_MAX_CHARS} characters: link records by ref instead of retyping them`,
      "body",
    )
  const to = c.to ?? agent.ownerId
  if (
    c.to !== undefined &&
    (!tx.state.members.some((m) => m.userId === c.to) || !isWorkingMember(tx.state, c.to))
  )
    invalid("a brief goes to a member of this project", "to")
  tx.emit({
    _tag: "BriefSent",
    agentId: agent.id,
    to,
    body,
    at: tx.now as never,
    ...(sections ? { sections } : {}),
  })
}

/**
 * A KPI sample (decision 0045). People: evidence optional (a person corrects a sample by recording the same period
 * again). Agents and workers: with evidence; an agent must be the lead or the agent of the KPI's area, a worker's item
 * must name the KPI. Decision 0097: a KPI fed by a recipe (or the never-filled `connector` source) takes samples like a
 * manual one, from people, agents and workers; only a `formula` KPI is derived and takes none. `at` is stored rounded
 * down to the start of the KPI's cadence period (UTC hour, day or ISO-week Monday), so a newer sample for the same period
 * wins in the fold and in the series; the log keeps both.
 */
const kpiSampleEvent = (
  tx: Tx,
  c: Omit<Cmd<"RecordKpiSample">, "_tag">,
  path = "",
): Extract<ProjectEvent, { _tag: "KpiSampleRecorded" }> => {
  const k = kpiOf(tx.state, c.kpiId) ?? notFound({ _tag: "kpi", id: c.kpiId })
  const a = tx.actor
  if (k.source._tag === "formula")
    forbidden("actor_kind", `${k.ref} is derived by its formula: record its inputs instead`)
  if (c.sample.at > KPI_AT_MAX) invalid("at is past the latest date a sample can name", `${path}sample.at`)
  const evidence = c.evidence ?? []
  if (a._tag === "agent" || a._tag === "worker") {
    if (evidence.length === 0)
      invalid(
        `a KPI sample from an agent needs evidence: the query, the tool call, a URL or a note${path ? ` (${k.ref})` : ""}`,
        `${path}evidence`,
      )
    if (a._tag === "agent") {
      const agent = agentOf(tx.state, a.agentId)
      const areaAgent = agent !== undefined && k.areaId !== null && agent.areaId === k.areaId
      if (!isLeadActor(tx) && !areaAgent)
        forbidden(
          "not_owner",
          `only the lead or the agent of ${k.ref}'s area records it; ask_human or tell the lead`,
        )
    } else {
      const item = itemOf(tx.state, a.itemId)
      if (!item || !itemNamesKpi(tx.state, item, k))
        forbidden(
          "not_owner",
          `${item?.ref ?? "this item"} doesn't name ${k.ref}: a worker records only the KPIs its item names (its goal, its bet, or ${k.ref} in the description)`,
        )
    }
  }
  for (const [i, e] of evidence.entries())
    if (e._tag === "artifact" && !artifactOf(tx.state, e.artifactId))
      invalid(`${e.artifactId} is not an artifact of this project`, `${path}evidence[${i}].artifactId`)
  return {
    _tag: "KpiSampleRecorded",
    kpiId: k.id,
    sample: { ...c.sample, at: kpiPeriodStart(k.cadence, c.sample.at) as typeof c.sample.at },
    ...(evidence.length > 0 ? { evidence } : {}),
  }
}

export const recordKpiSample = (tx: Tx, c: Cmd<"RecordKpiSample">): void => {
  tx.emit(kpiSampleEvent(tx, c))
}

/** Decision 0097: many samples in one command, each checked as one; a refused sample refuses them all. */
export const recordKpiSamples = (tx: Tx, c: Cmd<"RecordKpiSamples">): void => {
  const events = c.samples.map((x, i) => kpiSampleEvent(tx, x, `samples[${i}].`))
  for (const e of events) tx.emit(e)
}

/** [P3] An evaluator agent that does not own the bet records its verdict. */
export const recordBetVerdict = (tx: Tx, c: Cmd<"RecordBetVerdict">): void => {
  const i = requireItem(tx, c.itemId)
  if (i.bet === null) invalid("only bets get verdicts", "itemId")
  const a = tx.actor
  if (a._tag !== "agent" || ownsItem(i, a.agentId))
    forbidden("not_owner", "a bet's verdict comes from an evaluator that does not own it")
  tx.emit({
    _tag: "BetVerdictRecorded",
    itemId: i.id,
    value: c.verdict,
    by: (a as Extract<Actor, { _tag: "agent" }>).agentId,
    evidence: c.evidence,
  })
}

/** The watchdog escalates once per subject and signal; it never creates items. */
export const escalateStall = (tx: Tx, c: Cmd<"EscalateStall">): void => {
  if (!recordOf(tx.state, c.subject)) notFound(c.subject)
  for (const o of c.options)
    for (const e of o.effects)
      if (!isSystemEffect(e))
        invalid(`a system escalation may only offer flow effects, not ${e._tag}`, "options")
  const subjectId = c.subject.id
  const intentKey = `stall:${subjectId}:${c.signal}`
  const dup = tx.state.proposals.find((p) => p.intentKey === intentKey && isOpenProposal(p))
  if (dup)
    reject({
      _tag: "Duplicate",
      existing: { _tag: "proposal", id: dup.id },
      reason: "this stall is already escalated",
    })
  const item =
    c.subject._tag === "item"
      ? itemOf(tx.state, c.subject.id)
      : c.subject._tag === "run"
        ? (() => {
            const r = runOf(tx.state, c.subject.id as Ids["RunId"])
            return r?.itemId ? itemOf(tx.state, r.itemId) : undefined
          })()
        : undefined
  const owner = item ? escalationTarget(tx.state, item) : tx.state.project?.ownerId
  if (!owner) invalid("nobody to escalate to", "subject")
  const cause = c.signal.startsWith("loop") ? ("loop" as const) : ("stall" as const)
  tx.emit({ _tag: "StallDetected", subject: c.subject, signal: c.signal, proposalId: c.proposalId })
  openProposal(tx, {
    id: c.proposalId,
    ownerId: owner,
    body: {
      _tag: "question",
      prompt: `${c.signal} on ${c.subject._tag} ${subjectId}`,
      subject: c.subject,
      options: c.options.length > 0 ? c.options : item ? escalationOptions(item, cause) : [],
      allowFreeText: true,
      defaultOnExpiry: null,
      answersAsk: null,
      escalation: { cause, detail: c.signal },
    },
    proposedBy: tx.actor,
    reasoning: `The watchdog saw ${c.signal}.`,
    oneLine: `Stalled: ${c.signal}`,
    risk: "medium",
    reversibility: "reversible",
    tier: item?.status === "in_progress" ? "blocking" : "soon",
    blocks: item ? [{ _tag: "item", id: item.id }] : [],
    tainted: false,
    approver: { _tag: "human", userId: owner },
    decideBy: null,
    expiresAt: null,
    intentKey,
  })
}

/** Past this many held wakes the chain card only counts ("and 3 more"). */
const HELD_MAX = 20
const MORE = / \(and (\d+) more\)$/

/**
 * Wave A §4.0.1 (system:watchdog): a wake that would reach the agent's optional `chainCap` (agent-caused wakes in a
 * row) becomes, or joins,
 * one card per agent for its owner (the one who would pay for the turn): Continue delivers the held wakes, Stop drops
 * them. It creates no item and no work; `StallDetected` says in the log why the card exists.
 */
export const holdWake = (tx: Tx, c: Cmd<"HoldWake">): void => {
  const agent = agentOf(tx.state, c.agentId)
  if (!agent || agent.status === "retired") invalid("no such agent to hold a wake for", "agentId")
  // wave C (decision 0071): the day's timed-turn cap has its own card (`timed:<agentId>:<day>`)
  if (c.hold === "timed") {
    holdTimed(tx, {
      proposalId: c.proposalId,
      agent,
      reason: c.reason,
      cause: c.cause,
      day: projectDay(tx.state, tx.now),
    })
    return
  }
  const from = c.from ? agentOf(tx.state, c.from) : undefined
  const chain = chainOfReason(c.reason)
  const intentKey = `chain:${agent.id}`
  const entry = { agentId: agent.id, reason: c.reason, cause: c.cause }
  const open = tx.state.proposals.find((p) => p.intentKey === intentKey && isOpenProposal(p))
  // decision 0070 (TRI-25): plain words for people, no mechanism ("went back and forth", "without a person")
  const oneLine = `${agent.name} and ${from?.name ?? "another agent"} asked each other ${chain} times in a row`
  // the escalation's detail goes under the card's title (oneLine), so it never repeats it; the prompt is the whole
  // sentence. `(and N more)` stays last: a joined card reads it back (MORE).
  const text = (count: number, extra: number) =>
    `Continue, or stop here? Last: ${heldLine(tx.state, c.reason, tx.ctx.history?.comment)}${
      count > 1 ? `. ${count} messages are waiting` : ""
    }${extra > 0 ? ` (and ${extra} more)` : ""}`
  const promptOf = (detail: string) => `${oneLine}. ${detail}`
  const signal = `loop: agent chain ${chain}`
  if (open?.body._tag === "question" && open.body.escalation) {
    const esc = open.body.escalation
    const held = esc.held ?? []
    const extraBefore = Number(MORE.exec(esc.detail)?.[1] ?? 0)
    const room = held.length < HELD_MAX
    const nextHeld = room ? [...held, entry] : held
    const extra = room ? extraBefore : extraBefore + 1
    const detail = text(nextHeld.length + extra, extra)
    const body = {
      ...open.body,
      prompt: promptOf(detail),
      escalation: { ...esc, detail, held: nextHeld },
    }
    tx.emit({ _tag: "StallDetected", subject: { _tag: "agent", id: agent.id }, signal, proposalId: open.id })
    tx.emit({
      _tag: "ProposalRevised",
      proposalId: open.id,
      revision: open.revision + 1,
      contentHash: contentHash(body),
      body,
      reasoning: open.reasoning,
    })
    return
  }
  const detail = text(1, 0)
  tx.emit({
    _tag: "StallDetected",
    subject: { _tag: "agent", id: agent.id },
    signal,
    proposalId: c.proposalId,
  })
  openProposal(tx, {
    id: c.proposalId,
    ownerId: agent.ownerId,
    body: {
      _tag: "question",
      prompt: promptOf(detail),
      subject: { _tag: "agent", id: agent.id },
      options: [
        { key: "continue", label: "Continue", detail: "Let them carry on", effects: [] },
        {
          key: "stop",
          label: "Stop",
          detail: "Stop here; both wait for a person or something new",
          effects: [],
        },
      ],
      allowFreeText: false,
      defaultOnExpiry: null,
      answersAsk: null,
      escalation: { cause: "loop", detail, held: [entry] },
    },
    proposedBy: tx.actor,
    reasoning: `The watchdog held a wake: ${signal}.`,
    oneLine,
    risk: "low",
    reversibility: "reversible",
    tier: "soon",
    blocks: [],
    tainted: false,
    approver: { _tag: "human", userId: agent.ownerId },
    decideBy: null,
    expiresAt: null,
    intentKey,
  })
}

const ORDINAL = (n: number): string => {
  const t = n % 100
  if (t >= 11 && t <= 13) return `${n}th`
  const suffix = n % 10 === 1 ? "st" : n % 10 === 2 ? "nd" : n % 10 === 3 ? "rd" : "th"
  return `${n}${suffix}`
}

/** A timed wake in one line: the monitor's instruction, or the series that is due. */
const timedLine = (s: ProjectState, r: Extract<Command, { _tag: "HoldWake" }>["reason"]): string => {
  if (r._tag === "monitor") {
    const m = monitorOf(s, r.monitorId)
    const text = (m?.instruction ?? "").split("\n")[0]?.trim() ?? ""
    return text ? `the monitor "${text.length > 120 ? `${text.slice(0, 119)}…` : text}"` : "a monitor"
  }
  if (r._tag === "series_due") {
    const t = itemOf(s, r.itemId)
    return `${t ? `${t.ref} ${t.title}` : "a recurring item"} is due for ${r.period}`
  }
  return r._tag.replace(/_/g, " ")
}

/**
 * Wave C (decision 0071, spec §7.3): a timed wake (a monitor fire or a due series) past the agent's daily cap waits on
 * one card per agent and day, `timed:<agentId>:<day>`, for its keeper: Continue today delivers the held wakes and lifts
 * the cap for the rest of the day (evolve), Stop for today drops them. A later held wake that day joins the card.
 */
export const holdTimed = (
  tx: Tx,
  h: {
    readonly proposalId: Ids["ProposalId"]
    readonly agent: Agent
    readonly reason: Extract<Command, { _tag: "HoldWake" }>["reason"]
    readonly cause: Extract<Command, { _tag: "HoldWake" }>["cause"]
    readonly day: string
  },
): void => {
  const { agent } = h
  const intentKey = timedIntentKey(agent.id, h.day)
  const entry = { agentId: agent.id, reason: h.reason, cause: h.cause }
  const cap = timedCapOf(agent)
  if (cap === null) invalid("this agent has no daily turn cap", "hold")
  const oneLine = `${agent.name}'s monitors want a ${ORDINAL(cap + 1)} turn today`
  const detailOf = (waiting: number, extra: number) =>
    `${cap} timed turns a day at most. Last: ${timedLine(tx.state, h.reason)}${
      waiting > 1 ? `. ${waiting} turns are waiting` : ""
    }${extra > 0 ? ` (and ${extra} more)` : ""}`
  const signal = `timed cap: ${agent.handle} ${h.day}`
  const open = tx.state.proposals.find((p) => p.intentKey === intentKey && isOpenProposal(p))
  // one card per agent and day: after Stop for today, later timed wakes that day are dropped (the fire is in the log)
  if (!open && tx.state.proposals.some((p) => p.intentKey === intentKey)) return
  if (open?.body._tag === "question" && open.body.escalation) {
    const esc = open.body.escalation
    const held = esc.held ?? []
    const extraBefore = Number(MORE.exec(esc.detail)?.[1] ?? 0)
    const room = held.length < HELD_MAX
    const nextHeld = room ? [...held, entry] : held
    const extra = room ? extraBefore : extraBefore + 1
    const detail = detailOf(nextHeld.length + extra, extra)
    const body = {
      ...open.body,
      prompt: `${open.oneLine}. ${detail}`,
      escalation: { ...esc, detail, held: nextHeld },
    }
    tx.emit({ _tag: "StallDetected", subject: { _tag: "agent", id: agent.id }, signal, proposalId: open.id })
    tx.emit({
      _tag: "ProposalRevised",
      proposalId: open.id,
      revision: open.revision + 1,
      contentHash: contentHash(body),
      body,
      reasoning: open.reasoning,
    })
    return
  }
  if (tx.state.proposals.some((p) => p.id === h.proposalId))
    invalid("proposal id already exists", "proposalId")
  const detail = detailOf(1, 0)
  tx.emit({
    _tag: "StallDetected",
    subject: { _tag: "agent", id: agent.id },
    signal,
    proposalId: h.proposalId,
  })
  openProposal(tx, {
    id: h.proposalId,
    ownerId: agent.ownerId,
    body: {
      _tag: "question",
      prompt: `${oneLine}. ${detail}`,
      subject: { _tag: "agent", id: agent.id },
      options: [
        {
          key: "continue",
          label: "Continue today",
          detail: "Take the waiting turns; no cap for the rest of today",
          effects: [],
        },
        {
          key: "stop",
          label: "Stop for today",
          detail: "Drop them; the clock starts again tomorrow",
          effects: [],
        },
      ],
      allowFreeText: false,
      defaultOnExpiry: null,
      answersAsk: null,
      escalation: { cause: "timed_cap", detail, held: [entry] },
    },
    proposedBy: tx.actor,
    reasoning: `${agent.name} reached the daily cap of ${cap} timed turns.`,
    oneLine,
    risk: "low",
    reversibility: "reversible",
    tier: "soon",
    blocks: [],
    tainted: false,
    approver: { _tag: "human", userId: agent.ownerId },
    decideBy: null,
    expiresAt: null,
    intentKey,
  })
}

/**
 * The overdue alarm (G6, system:scheduler): a person's item past its due date while it holds others → ItemOverdue,
 * once per due date. Anything else is Invalid (a second note for the same due date writes nothing).
 */
export const noteOverdue = (tx: Tx, c: Cmd<"NoteOverdue">): void => {
  const t = requireItem(tx, c.itemId)
  if (!overdueCandidates(tx.state, tx.now).some((x) => x.id === t.id) || t.dueAt === null)
    invalid(`${t.ref} is not overdue, or already noticed for this due date`, "itemId")
  tx.emit({ _tag: "ItemOverdue", itemId: t.id, dueAt: t.dueAt })
}
